import {
  Location,
  LocationType,
  useLocationSearchQuery,
} from "../../api/locationApi.ts";
import { useCallback, useEffect, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import FormField from "../FormField.tsx";
import { t } from "@lingui/core/macro";
import { TooltipText } from "../TooltipText.tsx";

const DEBOUNCE_TIMEOUT_MS = 1000;

interface AddressSearchFieldProps {
  selectedAddress: Location | null;
  onAddressSelect: (address: Location) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  locationType: LocationType;
}

export default function AddressSearch({
  selectedAddress,
  onAddressSelect,
  placeholder = "Search for an address...",
  className = "",
  locationType,
}: AddressSearchFieldProps) {
  const [addressQuery, setAddressQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);

  const { data: addressResults = [], isLoading: addressSearchLoading } =
    useLocationSearchQuery(debouncedQuery || undefined);

  // Format address for prefilling input
  const formatAddressForInput = (addr: Location) => {
    const parts = [];
    if (addr.streetName && addr.houseNumber) {
      parts.push(`${addr.streetName} ${addr.houseNumber}`);
    } else if (addr.streetName) {
      parts.push(addr.streetName);
    }

    const cityPart = [];
    if (addr.postalCode) cityPart.push(addr.postalCode);
    if (addr.city) cityPart.push(addr.city);
    if (cityPart.length > 0) parts.push(cityPart.join(" "));

    if (addr.countryCode) parts.push(addr.countryCode);

    return parts.join(", ");
  };

  // Update input field when selectedAddress changes and not in editing mode
  useEffect(() => {
    if (selectedAddress) {
      setAddressQuery(formatAddressForInput(selectedAddress));
    } else if (!selectedAddress) {
      setAddressQuery("");
    }
  }, [selectedAddress]);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(addressQuery);
    }, DEBOUNCE_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [addressQuery]);

  const handleAddressSearch = useCallback((value: string) => {
    setAddressQuery(value);
    setShowAddressDropdown(value.length > 0);
  }, []);

  const handleAddressSelect = useCallback(
    (address: Location) => {
      setAddressQuery(formatAddressForInput(address));
      setShowAddressDropdown(false);
      onAddressSelect(address);
    },
    [onAddressSelect],
  );

  const handleInputFocus = useCallback(() => {
    setShowAddressDropdown(addressQuery.length > 0);
  }, [addressQuery]);

  const handleInputBlur = useCallback(() => {
    // Use setTimeout to allow click events on dropdown items to fire first
    setTimeout(() => {
      setShowAddressDropdown(false);
    }, 150);
  }, []);

  const formatAddressDisplay = (addr: Location) => {
    const parts = [addr.streetName, addr.houseNumber, addr.city].filter(
      Boolean,
    );
    return parts.join(" ");
  };

  return (
    <div className={`mb-6 ${className}`}>
      {/* Address search interface - always visible */}
      <div className={"relative"}>
        <FormField htmlFor={"address-search"} label={t`Address`}>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={addressQuery}
              onChange={(e) => handleAddressSearch(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              className="focus:border-soluud-primary focus:ring-soluud-primary/30 w-full rounded-lg border border-gray-300 py-2 pr-10 pl-10 transition-colors duration-150 focus:ring-2 focus:outline-none"
              id={"address-search"}
            />
          </div>

          {/* Additional address details when not editing */}
          {selectedAddress && (
            <div className="mt-2 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3 text-gray-400" />
                <span>
                  <span>
                    <Trans>Time Zone: </Trans>
                    {selectedAddress.timeZone}
                  </span>
                </span>
              </div>
            </div>
          )}

          <TooltipText
            tooltip={
              locationType == "REMOTE"
                ? t`An address is required for remote locations to determine your timezone. The timezone is needed for appointment scheduling with your scholars.`
                : t`An address is required so that the scholar knows where to go for appointments.`
            }
            position={"right"}
          >
            <span className={"mt-2 text-xs text-gray-600"}>
              <Trans>Why is an address required?</Trans>
            </span>
          </TooltipText>

          {/* Search Results Dropdown */}
          {showAddressDropdown && (
            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
              {addressSearchLoading ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-900"></div>
                  <Trans>Searching...</Trans>
                </div>
              ) : addressResults.length > 0 ? (
                addressResults.map((address) => (
                  <button
                    key={address.id}
                    type="button"
                    onClick={() => handleAddressSelect(address)}
                    className="w-full border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {formatAddressDisplay(address)}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {[address.postalCode, address.countryCode]
                            .filter(Boolean)
                            .join(" • ")}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              ) : addressQuery.length > 0 && debouncedQuery ? (
                <div className="px-4 py-3 text-sm text-gray-500">
                  <Trans>No addresses found</Trans>
                </div>
              ) : null}
            </div>
          )}
        </FormField>
      </div>
    </div>
  );
}
